import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import { Eye, LogOut, RefreshCw, Activity } from "lucide-react";
import LiveFeed from "@/components/shared/LiveFeed";

export default function ViewerDashboard() {
  const { session, hubData, refreshData, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const nav = useNavigate();
  const { events, drivers, requests, machines, sites } = hubData;

  const activeDrivers = drivers.filter(d => d.status === "ACTIVE");
  const newReqs = requests.filter(r => r.status === "NEW").length;
  const inProgress = requests.filter(r => r.status === "IN_PROGRESS").length;
  const done = requests.filter(r => r.status === "DONE").length;
  const totalLiters = requests.filter(r => r.status === "DONE").reduce((s, r) => s + (r.delivered_diesel || 0), 0);

  return (
    <div className="min-h-[100dvh] bg-slate-950 flex flex-col" data-testid="viewer-dashboard">
      <header className="bg-slate-950 px-6 py-4 flex items-center justify-between border-b border-slate-800 sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-700 rounded-md flex items-center justify-center text-white shadow-lg">
            <Eye className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-100">{t("viewer")} Mode</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500">{session?.hubName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refreshData} className="p-2.5 bg-slate-800 border border-slate-700 rounded-md text-slate-400 hover:text-white transition-all">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { logout(); nav("/"); }}
            className="p-2.5 bg-red-600/10 border border-red-600/20 rounded-md text-red-500 hover:bg-red-600 hover:text-white transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="flex-1 p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Active Drivers", value: activeDrivers.length, color: "text-lime-400" },
            { label: "New Requests", value: newReqs, color: "text-amber-400" },
            { label: "In Progress", value: inProgress, color: "text-sky-400" },
            { label: "Total Diesel", value: `${totalLiters}L`, color: "text-lime-400" },
          ].map((stat, i) => (
            <div key={i} className="bg-slate-900 border border-slate-800 rounded-md p-5 text-center">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">{stat.label}</span>
              <span className={`text-2xl font-black font-mono ${stat.color} mt-1 block`}>{stat.value}</span>
            </div>
          ))}
        </div>

        <LiveFeed events={events} />

        {/* Active Drivers */}
        <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2 mb-4">
            <Activity className="w-3.5 h-3.5 text-lime-500" /> Active Drivers
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {drivers.map(d => (
              <div key={d.driver_name} className={`p-4 rounded-md border flex items-center gap-3 ${d.status === "ACTIVE" ? "border-lime-500/30 bg-lime-500/5" : "border-slate-800 bg-slate-950 opacity-60"}`}>
                <div className={`w-10 h-10 rounded-md flex items-center justify-center font-black text-lg ${d.status === "ACTIVE" ? "bg-lime-500 text-slate-950" : "bg-slate-700 text-white"}`}>
                  {d.driver_name[0]}
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-100 uppercase tracking-tight block">{d.driver_name}</span>
                  <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">{d.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Requests */}
        <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2 mb-4">
            <Activity className="w-3.5 h-3.5 text-lime-500" /> Recent Requests
          </h3>
          <div className="space-y-2">
            {requests.slice(0, 10).map(req => (
              <div key={req.id} className="p-3 bg-slate-950 border border-slate-800 rounded-md flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${req.status === "DONE" ? "bg-lime-500" : req.status === "IN_PROGRESS" ? "bg-amber-500 animate-pulse" : "bg-slate-500"}`} />
                  <div>
                    <span className="text-xs font-bold text-slate-200">{req.machine_number}</span>
                    <span className="text-[10px] text-slate-500 ml-2">{req.site_name}</span>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-slate-500 uppercase">{req.status}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
